import type { DataProvider } from '@refinedev/core';
import { axiosInstance } from './axiosInstance';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async ({ resource, pagination, filters, sorters }) => {
    const { current = 1, pageSize = 10 } = pagination ?? {};

    const params: Record<string, string | number> = {
      page: current,
      limit: pageSize,
    };

    // Map Refine filters → query params
    filters?.forEach((filter) => {
      if ('field' in filter) {
        params[filter.field] = filter.value as string | number;
      }
    });

    // Map Refine sorters → sort/order params
    if (sorters && sorters.length > 0) {
      params.sort = sorters[0].field;
      params.order = sorters[0].order;
    }

    const { data } = await axiosInstance.get(`/api/v1/${resource}`, { params });

    return {
      data: data.data ?? data.items ?? data,
      total: data.total ?? data.meta?.total ?? 0,
    };
  },

  getOne: async ({ resource, id }) => {
    const { data } = await axiosInstance.get(`/api/v1/${resource}/${id}`);
    return { data: data.data ?? data };
  },

  create: async ({ resource, variables }) => {
    const { data } = await axiosInstance.post(`/api/v1/${resource}`, variables);
    return { data: data.data ?? data };
  },

  update: async ({ resource, id, variables }) => {
    const { data } = await axiosInstance.put(`/api/v1/${resource}/${id}`, variables);
    return { data: data.data ?? data };
  },

  deleteOne: async ({ resource, id }) => {
    const { data } = await axiosInstance.delete(`/api/v1/${resource}/${id}`);
    return { data: data.data ?? data };
  },

  getMany: async ({ resource, ids }) => {
    const responses = await Promise.all(
      ids.map((id) => axiosInstance.get(`/api/v1/${resource}/${id}`)),
    );
    return { data: responses.map((r) => r.data.data ?? r.data) };
  },

  custom: async ({ url, method = 'get', payload, headers, query }) => {
    const { data } = await axiosInstance({
      url,
      method,
      data: payload,
      headers: headers as Record<string, string>,
      params: query,
    });
    return { data };
  },
};
